import { useState, useEffect, useRef } from 'react'
import { Check, ListChecks, FolderOpen, NotebookPen, Lock, Car } from 'lucide-react'
import { CATEGORIES, PRIORITIES } from '../../db/database'
import { todayISO } from '../../utils/dateUtils'
import { parseSmartInput } from '../../utils/smartParser'
import { useT, useLang } from '../../i18n/useT'
import { useFeature } from '../../config/features'
import { DateTimePicker } from '../DateTimePicker'
import SmartListModal from '../SmartListModal'
import useSmartListStore from '../../store/smartListStore'
import PracticePicker from '../PracticePicker'
import { useLivePractices } from '../../hooks/useSupabaseLiveQuery'
import FieldRow from './FieldRow'
import NotesEditorSheet from './NotesEditorSheet'
import LocationAddressInput from './LocationAddressInput'
import TrafficAlertModal from './TrafficAlertModal'
import useLocationReminderStore from '../../store/locationReminderStore'
import { scheduleTrafficRefresh } from '../../lib/checkTrafficReminder'

// Vecchio formato __checklist__ → testo semplice
function migrateNotes(notes) {
  if (!notes?.startsWith('__checklist__')) return notes || ''
  return notes.replace('__checklist__\n', '')
    .split('\n').filter(Boolean)
    .map(line => line.replace(/^- \[ \] /, '- ').replace(/^- \[x\] /, '- [x] '))
    .join('\n')
}

// Helper: preview tronca delle note per la riga del gruppo field-rows.
// Mostra solo la prima riga non vuota, max 40 char.
function notesPreview(text) {
  if (!text) return ''
  const firstLine = text.split('\n').find(l => l.trim()) || ''
  const stripped = firstLine.replace(/^[-*]\s+\[?[ x]?\]?\s*/, '').trim()
  return stripped.length > 40 ? stripped.slice(0, 40) + '…' : stripped
}

export default function ManualForm({ initialData, onSave, onClose, onToggle }) {
  const isEditing = !!initialData?.id
  const t         = useT()
  const lang      = useLang()
  const can       = useFeature()
  const touched   = useRef(new Set())
  const titleRef  = useRef(null)
  const [autoFilled, setAutoFilled] = useState(new Set())
  const createSmartList = useSmartListStore(s => s.createList)
  const loadSmartList   = useSmartListStore(s => s.loadForTask)
  // Quando l'utente crea via "+ smart list", salviamo l'id qui per:
  //  - mostrare smartList con il taskId nuovo
  //  - cambiare il bottone primario in "Fine"
  // Diverso da initialData?.id perché quella prop arriva dal parent e non
  // viene aggiornata dopo la creazione: usiamo questo state come fallback.
  const [savedTaskId, setSavedTaskId] = useState(null)
  const isPostCreate = !isEditing && !!savedTaskId
  const effectiveTaskId = initialData?.id || savedTaskId

  // Bottom-sheet aperti per Note + Smart List + Traffic (Pratica usa il proprio sheet
  // interno a PracticePicker tramite renderTrigger). Smart-list-busy è un
  // flag durante l'await tra "tap" e "apertura": evita doppi tap.
  const [notesSheetOpen, setNotesSheetOpen]     = useState(false)
  const [slSheetOpen, setSlSheetOpen]           = useState(false)
  const [slBusy, setSlBusy]                     = useState(false)
  const [trafficModalOpen, setTrafficModalOpen] = useState(false)
  const locationReminderLoadForTask = useLocationReminderStore(s => s.loadForTask)

  // Lista delle pratiche aperte — usata per decidere se mostrare la riga
  // "Pratica" (hide se nessuna aperta e nessuna selezionata).
  const practices = useLivePractices() ?? []

  // Cache smart-list del task corrente (per mostrare "done/total" o "Crea")
  const slCache = useSmartListStore(s => effectiveTaskId ? s.cache[effectiveTaskId] : null)
  useEffect(() => { if (effectiveTaskId) loadSmartList(effectiveTaskId).catch(() => {}) }, [effectiveTaskId])

  // Load location reminder in edit mode
  const locationReminder = useLocationReminderStore(s => effectiveTaskId ? s.cache[effectiveTaskId] : null)
  const trafficRefreshTriggeredRef = useRef(false)
  useEffect(() => {
    if (effectiveTaskId) {
      locationReminderLoadForTask(effectiveTaskId).catch(() => {})
      trafficRefreshTriggeredRef.current = false
    }
  }, [effectiveTaskId])

  // Populate trafficData when locationReminder loads and trigger traffic check only once
  useEffect(() => {
    if (locationReminder) {
      setTrafficData({
        originAddr: locationReminder.originAddress,
        originLat: locationReminder.originLat,
        originLon: locationReminder.originLon,
        destAddr: locationReminder.destinationAddress,
        destLat: locationReminder.destinationLat,
        destLon: locationReminder.destinationLon,
      })
      // Trigger traffic check only once when editing
      if (isEditing && locationReminder?.id && !trafficRefreshTriggeredRef.current) {
        trafficRefreshTriggeredRef.current = true
        scheduleTrafficRefresh(effectiveTaskId)
      }
    }
  }, [locationReminder?.id, isEditing])

  // Auto-grow del titolo: height segue il contenuto (wrap quando titolo lungo)
  const autoResizeTitle = (el) => {
    if (!el) return
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }
  useEffect(() => { autoResizeTitle(titleRef.current) }, [])

  const [form, setFormState] = useState({
    title:             initialData?.title             || '',
    category:          initialData?.category          || 'casa',
    dueDate:           initialData?.dueDate           || '',
    dueTime:           initialData?.dueTime           || '',
    priority:          initialData?.priority          || 'media',
    recurring:         initialData?.recurring         || false,
    recurringInterval: initialData?.recurringInterval || 1,
    recurringUnit:     initialData?.recurringUnit     || 'mesi',
    reminder:          initialData?.reminder          || false,
    reminderMinutes:   initialData?.reminderMinutes   || 15,
    durationMinutes:   initialData?.durationMinutes   || null,
    notes:             migrateNotes(initialData?.notes),
    practiceId:        initialData?.practiceId        || null,
  })

  // Traffic reminder data (stored separately from form)
  const [trafficData, setTrafficData] = useState(null)

  const setField = (key) => (val) => {
    touched.current.add(key)
    setAutoFilled(prev => { const s = new Set(prev); s.delete(key); return s })
    setFormState(f => ({ ...f, [key]: val }))
  }

  useEffect(() => {
    if (!form.title.trim() || isEditing || savedTaskId) return
    const timer = setTimeout(() => {
      const parsed = parseSmartInput(form.title, lang)
      if (!parsed) return
      const updates = {}
      const newAuto = new Set()
      if (!touched.current.has('category') && parsed.category && parsed.category !== form.category) {
        updates.category = parsed.category; newAuto.add('category')
      }
      if (!touched.current.has('dueDate') && parsed.dueDate && !form.dueDate) {
        updates.dueDate = parsed.dueDate;   newAuto.add('dueDate')
      }
      if (!touched.current.has('dueTime') && parsed.dueTime && !form.dueTime) {
        updates.dueTime = parsed.dueTime;   newAuto.add('dueTime')
      }
      if (!touched.current.has('priority') && parsed.priority !== 'media') {
        updates.priority = parsed.priority; newAuto.add('priority')
      }
      if (Object.keys(updates).length) {
        setFormState(f => ({ ...f, ...updates }))
        setAutoFilled(newAuto)
      }
    }, 600)
    return () => clearTimeout(timer)
  }, [form.title]) // eslint-disable-line

  const [saving, setSaving]   = useState(false)
  const [saveErr, setSaveErr] = useState('')

  // Esegue onSave con i valori correnti del form. Ritorna l'id del task
  // (nuovo se creazione, stesso se modifica) o null in caso di errore.
  // Non chiude il modale — lascia decidere al chiamante.
  const performSave = async () => {
    if (!form.title.trim() || saving) return null
    if (form.dueDate && form.dueDate < todayISO()) {
      setSaveErr(t('modal_err_past_date'))
      return null
    }
    setSaving(true)
    setSaveErr('')
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), 8000)
    )
    try {
      const payload = {
        title:             form.title.trim(),
        category:          form.category,
        dueDate:           form.dueDate  || null,
        dueTime:           form.dueTime  || null,
        priority:          form.priority,
        recurring:         form.recurring,
        recurringInterval: form.recurring ? Number(form.recurringInterval) : null,
        recurringUnit:     form.recurring ? form.recurringUnit : null,
        reminder:          form.reminder,
        reminderMinutes:   form.reminder ? Number(form.reminderMinutes) : 15,
        durationMinutes:   form.durationMinutes || null,
        notes:             form.notes.trim(),
        practiceId:        form.practiceId || null,
        trafficLocation:   trafficData,
      }
      // Post-create: propaga il target id così onSave aggiorna invece di creare
      if (savedTaskId) payload._targetId = savedTaskId
      const id = await Promise.race([onSave(payload), timeout])
      setSaving(false)
      return id
    } catch (err) {
      console.error('QuickAdd save error:', err)
      setSaving(false)
      if (err.message === 'task_limit') { onClose(); return null }
      setSaveErr(err.message === 'timeout' ? t('modal_err_network') : t('modal_err_save'))
      return null
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    // In tutti i casi: performSave gestisce create/update via _targetId; chiudi se ok.
    // In post-create eventuali edit ai campi del form vengono propagati come UPDATE
    // sul task appena creato (handleSave in App.jsx vede _targetId e usa updateTask).
    const id = await performSave()
    if (id) onClose()
  }

  // Handler tap riga Smart List. Edit mode: apre direttamente la sheet (la
  // modale gestisce createList lazy). Create mode: forza validazione HTML5
  // del form (mostra "campo obbligatorio" se titolo vuoto), poi salva task +
  // crea lista vuota, transiziona in post-create e apre la sheet.
  const handleOpenSmartList = async () => {
    if (slBusy) return
    if (effectiveTaskId) { setSlSheetOpen(true); return }
    const formEl = document.getElementById('quickadd-form')
    if (formEl && !formEl.reportValidity()) return
    setSlBusy(true)
    try {
      const newId = await performSave()
      if (!newId) return
      await createSmartList(newId)
      setSavedTaskId(newId)
      setSlSheetOpen(true)
    } catch (err) {
      console.error('SmartList create error:', err)
      setSaveErr(t('modal_err_save'))
    } finally {
      setSlBusy(false)
    }
  }

  // Stato derivato per le 3 field rows
  const slTotal     = slCache?.items?.length ?? 0
  const slDone     = slCache?.items?.filter(i => i.completed).length ?? 0
  const hasSlList  = !!slCache?.list
  const showPraticaRow = practices.some(p => p.status === 'open') || !!form.practiceId
  const notePreviewText = notesPreview(form.notes)

  const AutoTag = ({ field }) => autoFilled.has(field)
    ? <span className="auto-tag">✦ auto</span>
    : null

  return (
    <form id="quickadd-form" onSubmit={handleSubmit} className="modal-body">

      {/* Title */}
      <div className="form-group">
        <label>{t('modal_title_label')}</label>
        <textarea
          ref={titleRef}
          rows={1}
          autoFocus={!isEditing}
          value={form.title}
          onChange={e => { setField('title')(e.target.value); autoResizeTitle(e.target) }}
          onKeyDown={e => {
            // Enter = submit (come un input), Shift+Enter = newline manuale
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              e.currentTarget.form?.requestSubmit()
            }
          }}
          placeholder={t('modal_title_ph')}
          className="quickadd-title-input"
          maxLength={200}
          required
        />
      </div>

      {/* Category */}
      <div className="form-group">
        <label>{t('modal_category')} <AutoTag field="category" /></label>
        <div className="cat-pills">
          {Object.entries(CATEGORIES).map(([k, v]) => (
            <button type="button" key={k}
              className={`cat-pill${form.category === k ? ' active' : ''}`}
              style={{ color: v.color, background: form.category === k ? v.bg : 'var(--bg)' }}
              onClick={() => setField('category')(k)}
            >
              <v.Icon size={13} strokeWidth={2.2} /> {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Scadenza (data + ora) */}
      <div className="form-group">
        <label>{t('modal_due')} <AutoTag field="dueDate" /><AutoTag field="dueTime" /></label>
        <DateTimePicker
          value={form.dueDate}
          timeValue={form.dueTime}
          onDateChange={v => setField('dueDate')(v)}
          onTimeChange={v => setField('dueTime')(v)}
          reminderValue={form.reminder ? String(form.reminderMinutes) : ''}
          onReminderChange={v => {
            setField('reminder')(!!v)
            if (v) setField('reminderMinutes')(v)
          }}
          durationValue={form.durationMinutes ? String(form.durationMinutes) : ''}
          onDurationChange={v => setField('durationMinutes')(v ? Number(v) : null)}
          lang={lang}
          minISO={todayISO()}
          locationReminder={locationReminder}
        />        
      </div>

      {/* Priorità */}
      <div className="form-group">
        <label>{t('modal_priority')} <AutoTag field="priority" /></label>
        <div className="priority-row">
          {Object.entries(PRIORITIES).map(([k, v]) => (
            <div key={k}
              className={`priority-opt${form.priority === k ? ' active' : ''}`}
              style={{ color: form.priority === k ? v.color : undefined }}
              onClick={() => setField('priority')(k)}
            >
              {v.label}
            </div>
          ))}
        </div>
      </div>

      {/* Gruppo "Pratica / Smart List / Note" — 3 righe in un'unica card,
          ciascuna apre il proprio bottom-sheet al tap. Pratica visibile solo
          se l'utente ha almeno una pratica aperta o ne ha già una linkata. */}
      <div className="form-group" style={{ marginTop: 4 }}>
        <div className="field-rows">
          {showPraticaRow && (
            <PracticePicker
              value={form.practiceId}
              onChange={v => setField('practiceId')(v)}
              renderTrigger={({ open, selected }) => (
                <FieldRow
                  icon={<FolderOpen size={16} />}
                  label={t('practice_field_label')}
                  value={selected ? selected.title : t('practice_select_none')}
                  onClick={open}
                />
              )}
            />
          )}
          <FieldRow
            icon={<ListChecks size={16} />}
            label={t('sl_title')}
            value={hasSlList ? `${slDone}/${slTotal}` : t('sl_create')}
            accent={!hasSlList}
            onClick={handleOpenSmartList}
            disabled={slBusy}
          />
          <FieldRow
            icon={<NotebookPen size={16} />}
            label={t('modal_notes')}
            value={notePreviewText || t('notes_add')}
            accent={!form.notes}
            onClick={() => setNotesSheetOpen(true)}
          />
        </div>
      </div>

      {notesSheetOpen && (
        <NotesEditorSheet
          value={form.notes}
          onChange={v => setField('notes')(v)}
          onClose={() => setNotesSheetOpen(false)}
        />
      )}
      {slSheetOpen && effectiveTaskId && (
        <SmartListModal
          taskId={effectiveTaskId}
          taskTitle={null}
          onClose={() => setSlSheetOpen(false)}
        />
      )}

      {trafficModalOpen && (
        <TrafficAlertModal
          initialData={trafficData}
          onSave={(newData) => setTrafficData(newData)}
          onClose={() => setTrafficModalOpen(false)}
        />
      )}

      {/* Traffic Alert Block */}
      <div className="form-group" style={{ marginTop: 4, opacity: (form.reminder && form.dueDate && form.dueTime && can('traffic_alerts')) ? 1 : 0.6 }}>
        <div className="field-rows">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
            <FieldRow
              icon={<Car size={16} />}
              label={t('traffic_alert_label')}
              value={trafficData ? `${trafficData.originAddr?.split(',')[0]}` : t('traffic_add')}
              accent={!trafficData}
              onClick={() => (form.reminder && form.dueDate && form.dueTime && can('traffic_alerts')) && setTrafficModalOpen(true)}
              disabled={!form.reminder || !form.dueDate || !form.dueTime || !can('traffic_alerts')}
              style={{ flex: 1 }}
            />            
          </div>
          {!can('traffic_alerts') && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 8 }}>
              {t('premium_feature_badge')}
            </div>
          )}
        </div>
      </div>

      {saveErr && (
        <div className="form-error" style={{ marginTop: 8 }}>{saveErr}</div>
      )}
      <button type="submit" className="btn btn-primary" style={{ marginTop: 4 }} disabled={saving}>
        {saving
          ? t('modal_saving')
          : isPostCreate
            ? t('modal_done')
            : isEditing
              ? t('modal_save_edit')
              : t('modal_save')}
      </button>

      {isEditing && onToggle && (
        <button
          type="button"
          className="btn btn-ghost"
          style={{ marginTop: 8, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          onClick={onToggle}
        >
          <Check size={16} />
          {initialData.completed ? t('modal_uncomplete') : t('modal_complete')}
        </button>
      )}
    </form>
  )
}
